import React, { useEffect } from 'react'
import {
  Button,
  TextField,
  Grid,
  Typography,
  Autocomplete,
  createFilterOptions,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { Controller, useWatch } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { useGetAccountsByHTG } from '../../hooks/api/accounts'
import htgCompanies from '../../constants/companies'
import { useMapFormContext } from './FormContext'
import Contracts from './Contracts'
import { useNavigate } from 'react-router-dom'
import { MAP_HTG_COMAPNIES, ALLOWED_TYPE_VALUES } from './schema'

const createCustomFilterOptions = (selectedValue) => {
  const baseFilter = createFilterOptions({ ignoreCase: true, matchFrom: 'any', limit: 10 })

  return (options, params) => {
    const filtered = baseFilter(options, params)

    if (selectedValue) return [selectedValue]

    return filtered
  }
}

const Form = ({ isCreateMode, defaultValues, api }) => {
  const { control, handleSubmit, reset, setValue } = useMapFormContext()
  const companyAccountId = useWatch({ control, name: 'companyAccountId' })
  const htgCompany = useWatch({ control, name: 'htgCompany' })
  const navigate = useNavigate()

  const { data: accs, isLoading: isLoadingAccount } = useGetAccountsByHTG({
    htg: MAP_HTG_COMAPNIES,
  })

  const accountOptions = React.useMemo(() => {
    if (!accs || typeof accs !== 'object') return []

    return Object.values(accs).flat()
  }, [accs])

  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync, isLoading: isSubmitting } = api()

  const onClose = () => navigate(-1)

  const onSubmit = async (values) => {
    try {
      await mutateAsync(values)
      enqueueSnackbar(`Successfully ${isCreateMode ? 'created' : 'updated'}`, {
        variant: 'success',
      })
      onClose()
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  useEffect(() => reset({ ...defaultValues }), [defaultValues, reset])

  return (
    <Grid container spacing={2} sx={{ flexGrow: 1, padding: 5 }}>
      <Grid item xs={12}>
        <Typography variant='h5' fontWeight={600}>
          {isCreateMode ? 'Create Map' : 'Edit Map'}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={'type'}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              options={ALLOWED_TYPE_VALUES}
              value={field.value || ''}
              disabled={!isCreateMode}
              onChange={(_, v) => field.onChange(v || '')}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Type'
                  variant='outlined'
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={'companyAccountId'}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const selectedAccount =
              accountOptions.find(
                (c) =>
                  c.customerId === companyAccountId && htgCompanies[c.htg]?.htgShort === htgCompany,
              ) || null
            return (
              <Autocomplete
                options={accountOptions}
                filterOptions={createCustomFilterOptions(selectedAccount)}
                getOptionLabel={(o) =>
                  `${o.customerId} - ${o.name} - ${htgCompanies[o.htg]?.htgShort}`
                }
                loading={isLoadingAccount}
                value={selectedAccount}
                disabled={!isCreateMode}
                fullWidth
                onChange={(_, v) => {
                  field.onChange(v ? v.customerId : '')
                  setValue('htgCompany', v ? htgCompanies[v.htg]?.htgShort : '')
                  setValue('companyName', v ? v.name : '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Company'
                    variant='outlined'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )
          }}
        />
      </Grid>
      <Contracts />
      <Grid item xs={12}>
        <Controller
          name='isCompleted'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label='Completed'
              control={
                <Checkbox
                  checked={Boolean(field.value)}
                  onChange={(_, checked) => field.onChange(checked ? 1 : 0)}
                />
              }
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button disabled={isSubmitting} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type='submit'
          variant='contained'
          disabled={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {isCreateMode ? 'Create' : 'Save'}
        </Button>
      </Grid>
    </Grid>
  )
}

export default Form
